import { displayUrlsFor, type DeckItem } from './deck';

/**
 * Pure state behind PresentationCrossfade's two-layer crossfade — kept free
 * of React and the DOM so the transitions are unit-testable the same way
 * deck.ts and stage.ts are.
 *
 * One layer of the crossfade: a deck item plus the deck position it came
 * from, so a layer that's about to disappear can still report the right
 * "N of M" counter for the slide it's still showing.
 */
export interface CrossfadeLayer {
  item: DeckItem;
  index: number;
}

/**
 * `current` is the slide fading in (or already fully shown); `previous` is
 * the slide it's fading over, kept mounted until the crossfade settles.
 * `previous` is null once nothing needs to fade behind `current` any more.
 */
export interface CrossfadeState {
  current: CrossfadeLayer;
  previous: CrossfadeLayer | null;
}

export function initialCrossfade(layer: CrossfadeLayer): CrossfadeState {
  return { current: layer, previous: null };
}

/**
 * Moves to a new layer. The old `current` becomes `previous` whether or not
 * its own fade-in had finished — if a second Next lands before the first
 * crossfade settles, the half-faded slide simply becomes the static
 * background the next one fades over, rather than leaving two layers both
 * mid-animation. Landing on the same item is a no-op so a re-render with an
 * unchanged deck doesn't restart the fade.
 */
export function advanceCrossfade(state: CrossfadeState, next: CrossfadeLayer): CrossfadeState {
  if (state.current.item === next.item) return state;
  return { current: next, previous: state.current };
}

/** Drops the previous layer once the crossfade has settled (or instantly,
 *  under reduced motion). A no-op if there's nothing to drop. */
export function clearPrevious(state: CrossfadeState): CrossfadeState {
  return state.previous ? { current: state.current, previous: null } : state;
}

/** Display URLs the slide at `index` needs on screen — empty for a title
 *  card, which has no photo to preload or wait on. */
export function targetImageUrls(item: DeckItem): string[] {
  return displayUrlsFor(item);
}

/**
 * Display URLs worth warming with a raw image preload ahead of a presenter
 * reaching them: the slide immediately before and after `index`, wrapping at
 * the ends the same way next()/prev() do. Title cards carry no photo and are
 * skipped, matching displayUrlsFor.
 */
export function neighborImageUrls(deck: DeckItem[], index: number): string[] {
  if (deck.length === 0) return [];
  const at = (offset: number) =>
    deck[(((index + offset) % deck.length) + deck.length) % deck.length];
  return [...displayUrlsFor(at(-1)), ...displayUrlsFor(at(1))];
}
